/**
 * User Management Page - HYB MASTER SHOP
 * Manage both application customers and web management admins
 */

import { Users, Search, RefreshCw, ShieldOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';
import { cn } from '../utils/responsive';
import {
  useUserManagement,
  AppUserSection,
  AdminUserSection,
  SecuritySection,
  SuspendUserModal,
  AppUserForm,
  AdminUserForm,
  EditAdminForm,
} from './users';

export function UserManagement() {
  const { hasPermission } = useAuth();
  const isMobileOrTablet = useIsMobileOrTablet();
  const {
    activeTab,
    setActiveTab,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    showModal,
    setShowModal,
    editAdmin,
    setEditAdmin,
    pdpaExpandedUser,
    pdpaLoadingUser,
    pdpaData,
    suspendModal,
    setSuspendModal,
    suspendReason,
    setSuspendReason,
    suspendReasonDetail,
    setSuspendReasonDetail,
    appUsers,
    adminUsers,
    isLoading,
    appTotal,
    adminTotal,
    handleToggleAppUser,
    handleConfirmSuspend,
    handleToggleAdminUser,
    handleOpenEditAdmin,
    handleSubmitEditAdmin,
    handleDeleteAdmin,
    handleTogglePdpaDetails,
    addAppForm,
    addAdminForm,
    editAdminForm,
    createAppUserMutation,
    createAdminUserMutation,
    updateAppUserMutation,
    appUsersQuery,
    adminUsersQuery,
    updateAdminDetailsMutation,
  } = useUserManagement();

  // Check permission - only admins can access User Management
  if (!hasPermission('write')) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-4">
          <ShieldOff className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">Require Admin role to access</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className={cn(
        "flex items-start gap-4",
        isMobileOrTablet ? "flex-col" : "items-center justify-between"
      )}>
        <div>
          <h1 className={cn(
            "font-bold text-stone-900 dark:text-gray-100 flex items-center gap-2",
            isMobileOrTablet ? "text-xl" : "text-2xl"
          )}>
            <Users className={cn(
              "text-yellow-600",
              isMobileOrTablet ? "w-5 h-5" : "w-6 h-6"
            )} />
            จัดการผู้ใช้งาน
          </h1>
          <p className={cn(
            "text-stone-600 dark:text-gray-400 mt-1",
            isMobileOrTablet ? "text-sm" : "text-base"
          )}>
            ควบคุมสิทธิ์การใช้งานทั้งฝั่งลูกค้าและทีมงานได้ในที่เดียว
          </p>
        </div>

        {/* Search & Filter Controls */}
        {activeTab !== 'security' && (
          <div className={cn(
            "flex gap-2 lg:gap-3",
            isMobileOrTablet ? "flex-col w-full" : "flex-wrap"
          )}>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 dark:text-gray-500" />
              <input
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="ค้นหาจากชื่อ / เบอร์"
                className={cn(
                  "w-full pl-9 pr-3 py-2 lg:py-2.5 border border-stone-300 dark:border-gray-600 rounded-lg",
                  "text-sm text-stone-900 dark:text-gray-100 placeholder:text-stone-400 dark:placeholder:text-gray-500",
                  "bg-white dark:bg-gray-800",
                  "focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:border-yellow-500",
                  "transition-all duration-200"
                )}
              />
            </div>
            <select
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value as any)}
              className={cn(
                "rounded-lg border border-stone-300 dark:border-gray-600 px-3 py-2 lg:py-2.5",
                "text-sm font-medium text-stone-700 dark:text-gray-100",
                "bg-white dark:bg-gray-800",
                "focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 focus:border-yellow-500",
                "transition-all duration-200",
                isMobileOrTablet && "w-full"
              )}
            >
              <option value="all">สถานะทั้งหมด</option>
              <option value="active">ใช้งานอยู่</option>
              <option value="inactive">ปิดใช้งาน</option>
              <option value="suspended">ถูกระงับ</option>
            </select>
            <button
              onClick={() => {
                if (activeTab === 'app') {
                  appUsersQuery.refetch();
                } else if (activeTab === 'admin') {
                  adminUsersQuery.refetch();
                }
              }}
              className={cn(
                "inline-flex items-center justify-center gap-2",
                "rounded-lg border border-stone-300 dark:border-gray-600 px-4 py-2 lg:py-2.5",
                "text-sm font-medium text-stone-700 dark:text-gray-100",
                "bg-white dark:bg-gray-800",
                "hover:bg-stone-50 dark:hover:bg-gray-700 hover:border-yellow-400",
                "transition-all duration-200 touch-target",
                isMobileOrTablet && "w-full"
              )}
            >
              <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
              รีเฟรช
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-stone-200 dark:border-gray-700 overflow-hidden">
        <nav className={cn(
          "flex border-b border-stone-200 dark:border-gray-700",
          isMobileOrTablet && "overflow-x-auto scrollbar-hide"
        )}>
          <button
            className={cn(
              "flex-1 px-3 lg:px-4 py-2.5 lg:py-3",
              "text-xs lg:text-sm font-medium border-b-2 transition-all duration-200",
              "whitespace-nowrap touch-target",
              activeTab === 'app'
                ? "border-yellow-500 text-yellow-600 dark:text-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/20"
                : "border-transparent text-stone-600 dark:text-gray-400 hover:text-stone-900 dark:hover:text-gray-100 hover:bg-stone-50 dark:hover:bg-gray-700"
            )}
            onClick={() => setActiveTab('app')}
          >
            ผู้ใช้ในแอป
          </button>
          <button
            className={cn(
              "flex-1 px-3 lg:px-4 py-2.5 lg:py-3",
              "text-xs lg:text-sm font-medium border-b-2 transition-all duration-200",
              "whitespace-nowrap touch-target",
              activeTab === 'admin'
                ? "border-yellow-500 text-yellow-600 dark:text-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/20"
                : "border-transparent text-stone-600 dark:text-gray-400 hover:text-stone-900 dark:hover:text-gray-100 hover:bg-stone-50 dark:hover:bg-gray-700"
            )}
            onClick={() => setActiveTab('admin')}
          >
            ผู้ใช้หลังบ้าน
          </button>
          <button
            className={cn(
              "flex-1 px-3 lg:px-4 py-2.5 lg:py-3",
              "text-xs lg:text-sm font-medium border-b-2 transition-all duration-200",
              "whitespace-nowrap touch-target",
              activeTab === 'security'
                ? "border-yellow-500 text-yellow-600 dark:text-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/20"
                : "border-transparent text-stone-600 dark:text-gray-400 hover:text-stone-900 dark:hover:text-gray-100 hover:bg-stone-50 dark:hover:bg-gray-700"
            )}
            onClick={() => setActiveTab('security')}
          >
            {isMobileOrTablet ? "เปลี่ยนรหัส" : "เปลี่ยนรหัสผ่าน Admin"}
          </button>
        </nav>
      </div>

      {activeTab === 'app' && (
        <AppUserSection
          users={appUsers}
          total={appTotal}
          isLoading={isLoading}
          isMobileOrTablet={isMobileOrTablet}
          onAddUser={() => setShowModal('app')}
          onToggleStatus={handleToggleAppUser}
          onTogglePdpa={handleTogglePdpaDetails}
          pdpaExpandedUser={pdpaExpandedUser}
          pdpaLoadingUser={pdpaLoadingUser}
          pdpaData={pdpaData}
        />
      )}
      {activeTab === 'admin' && (
        <AdminUserSection
          users={adminUsers}
          total={adminTotal}
          isLoading={isLoading}
          isMobileOrTablet={isMobileOrTablet}
          onAddUser={() => setShowModal('admin')}
          onToggleStatus={handleToggleAdminUser}
          onEdit={handleOpenEditAdmin}
          onDelete={handleDeleteAdmin}
        />
      )}
      {activeTab === 'security' && (
        <SecuritySection isMobileOrTablet={isMobileOrTablet} />
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/70 px-4">
          <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {showModal === 'app' ? 'เพิ่มลูกค้า' : 'เพิ่มพนักงาน'}
              </h2>
              <button onClick={() => setShowModal(null)} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">ปิด</button>
            </div>
            <div className="px-6 py-5">
              {showModal === 'app' ? (
                <AppUserForm
                  form={addAppForm}
                  onSubmit={addAppForm.handleSubmit(values => createAppUserMutation.mutate(values))}
                  isLoading={createAppUserMutation.isLoading}
                  onCancel={() => setShowModal(null)}
                />
              ) : (
                <AdminUserForm
                  form={addAdminForm}
                  onSubmit={addAdminForm.handleSubmit(values => createAdminUserMutation.mutate(values))}
                  isLoading={createAdminUserMutation.isLoading}
                  onCancel={() => setShowModal(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {editAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/70 px-4">
          <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">แก้ไขพนักงาน</h2>
              <button
                onClick={() => setEditAdmin(null)}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                ปิด
              </button>
            </div>
            <EditAdminForm
              form={editAdminForm}
              onSubmit={handleSubmitEditAdmin}
              isLoading={updateAdminDetailsMutation.isLoading}
              onCancel={() => setEditAdmin(null)}
              isDefaultUser={editAdmin.isDefault}
            />
          </div>
        </div>
      )}

      {suspendModal && (
        <SuspendUserModal
          user={suspendModal.user}
          reason={suspendReason}
          onReasonChange={setSuspendReason}
          detail={suspendReasonDetail}
          onDetailChange={setSuspendReasonDetail}
          onClose={() => setSuspendModal(null)}
          onConfirm={handleConfirmSuspend}
          isPending={updateAppUserMutation.isPending}
        />
      )}
    </div>
  );
}
